import { Link } from 'expo-router';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    isNonMobileWeb,
    LabelRegular,
    Sheet,
    Switch,
    XStack,
    YStack,
} from 'smoothie';

import { POLICY_DISCLOSURES_BATCH_OPENING, PolicyType } from '../Disclosures';

const TitleText = isNonMobileWeb ? BodyMediumEmphasized : BodySmallEmphasized;
const Text = isNonMobileWeb ? BodyMediumRegular : BodySmallRegular;
const FootnoteText = isNonMobileWeb ? BodySmallRegular : LabelRegular;

export const MoneyMapAutoPaySection = ({
    numberOfAccountsToOpen,
    memberHasAccountsToAccept,
    isChecked,
    onChange,
    enabled = true,
}: {
    numberOfAccountsToOpen: number;
    memberHasAccountsToAccept: boolean;
    isChecked: boolean;
    onChange: () => void;
    enabled: boolean;
}) => {
    if (!numberOfAccountsToOpen) {
        return null;
    }

    // AutoPay settings will apply to all cards *unless* the accounts were created by the partner
    // So if the current member has joint accounts to accept, then the AutoPay settings will not
    // apply to all cards.
    const isForAllCards = !memberHasAccountsToAccept;
    const isSingleAccountAction = numberOfAccountsToOpen === 1;
    const title = isForAllCards
        ? 'Enable AutoPay for all Fruitful Cards'
        : `Enable AutoPay for all Fruitful Cards linked to your individual ${isSingleAccountAction ? 'account' : 'accounts'}`;

    const description =
        'Enabling AutoPay means statement balances for all your Fruitful Cards, physical and virtual, are paid off automatically each month. Without AutoPay, payments for all cards have to be made manually.';

    return (
        <AutoPaySection
            title={title}
            description={description}
            enabled={enabled}
            isChecked={isChecked}
            onChange={onChange}
        />
    );
};

export const AutoPaySection = ({
    title,
    description,
    enabled,
    isChecked,
    onChange,
}: {
    title: string;
    description: string;
    enabled: boolean;
    isChecked: boolean;
    onChange: () => void;
}) => {
    return (
        <YStack
            data-section="auto pay"
            gap={16}
            width="100%"
            backgroundColor="$gray200"
            borderRadius={16}
            padding={16}
        >
            <XStack justifyContent="space-between" gap={16} alignItems="center">
                <TitleText disabled={!enabled} flex={1}>
                    {title}
                </TitleText>
                <Switch
                    isChecked={isChecked}
                    onCheckedChange={() => {
                        onChange();
                    }}
                    disabled={!enabled}
                />
            </XStack>
            <Text disabled={!enabled}>
                {description}
                {'  '}Find more details in your{' '}
                <Link
                    href={
                        POLICY_DISCLOSURES_BATCH_OPENING[
                            PolicyType.CARD_AGREEMENT
                        ].url
                    }
                    style={{
                        textDecorationLine: 'underline',
                    }}
                    target="_blank"
                >
                    <Text>
                        {
                            POLICY_DISCLOSURES_BATCH_OPENING[
                                PolicyType.CARD_AGREEMENT
                            ].description
                        }
                        .
                    </Text>
                </Link>
            </Text>
            <FootnoteText color="$gray1000">
                You can always change your AutoPay preferences within each
                individual account settings.
            </FootnoteText>
        </YStack>
    );
};

export const AutoPayDialog = ({
    isOpen,
    onClose,
    onNoAutoPay,
    onYesAutoPay,
}: {
    isOpen: boolean;
    onClose: () => void;
    onNoAutoPay: () => void;
    onYesAutoPay: () => void;
}) => {
    return (
        <Sheet
            isOpen={isOpen}
            onClose={onClose}
            title="Continue without AutoPay?"
            showCloseButton
            forceKeyboardCompatibleBottomSheet={true}
        >
            <YStack gap={20} width="100%" maxWidth={400} alignSelf="center">
                <BodyLargeEmphasized>
                    AutoPay allows you to automatically pay off your statement
                    balance each month.
                </BodyLargeEmphasized>
                <YStack paddingBottom={16} gap={4}>
                    <BodyMediumEmphasized>
                        If you pay manually:
                    </BodyMediumEmphasized>
                    <BodyMediumRegular>
                        You&apos;ll need to remember to pay the statement
                        balance off on the due date each month. Otherwise, your
                        card may be suspended.
                    </BodyMediumRegular>
                </YStack>
                <YStack gap={12} width="100%">
                    <Button
                        text="Yes, continue without AutoPay"
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.SECONDARY}
                        onPress={onNoAutoPay}
                    />
                    <Button
                        text="Enable AutoPay"
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.PRIMARY}
                        onPress={onYesAutoPay}
                    />
                </YStack>
            </YStack>
        </Sheet>
    );
};
